import { DestroyRef, inject, Injectable } from '@angular/core';

/**
 * Admin-wide guard against the browser's "scroll over a focused number input changes its value"
 * behaviour (PO 2026-08-12) — an unnoticed data-corruption trap: the operator scrolls the page,
 * the cursor happens to sit over a focused capacity / price field, and the value silently steps
 * (150 -> 149, 4000 -> 3900). A per-component opt-in would leave the admin inconsistent, so the
 * guard is installed once for every `input[type=number]` in the app.
 *
 * Two non-obvious constraints:
 * - the listener MUST be registered with `{ passive: false }`, because `wheel` on document
 *   defaults to passive, where `preventDefault()` is a no-op (an Angular `host` binding cannot
 *   express this, hence the manual `addEventListener`);
 * - CAPTURE phase, so the value change is killed before any component-level wheel handler runs.
 *
 * Trade-off: over a FOCUSED number input the page does not scroll either (one `preventDefault()`
 * kills both the value step and the scroll). Accepted — losing a scroll gesture is recoverable,
 * a silently edited price is not. Blurred inputs never step, so they are left alone and the page
 * scrolls normally over them.
 */
@Injectable({
  providedIn: 'root',
})
export class NumberInputWheelGuardService {

  private readonly destroyRef = inject(DestroyRef);

  private readonly onWheel = (event: WheelEvent): void => {
    if (!this.isFocusedNumberInput(event.target)) {
      return;
    }
    event.preventDefault();
  };

  init(): void {
    document.addEventListener('wheel', this.onWheel, { capture: true, passive: false });
    this.destroyRef.onDestroy(() => document.removeEventListener('wheel', this.onWheel, { capture: true }));
  }

  private isFocusedNumberInput(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLInputElement) || target.type !== 'number') {
      return false;
    }
    // Only a FOCUSED number input steps its value on wheel; an unfocused one just scrolls.
    return document.activeElement === target;
  }

}
